import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Meteor } from 'meteor/meteor';
import { useTracker } from 'meteor/react-meteor-data';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { PageFooter } from '/imports/ui/PageFooter';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { toastOnError } from '/imports/ui/utils';
import { Loading } from '/imports/ui/Loading';
import {
  SmaClientAccessToken, createSmaClientAccessToken, getSmaClientAccessTokensForOrg,
  pullSmaClientPlantsForToken,
} from '/imports/api/smaClientMeters';

const SmaClientMeterPageStyled = styled(Page)`
  .result { margin: 2rem 0; }
  table {
    td {
      padding: 1rem;
      &:first-child { padding-left: 0; }
    }
  }
`;

/** Super-user page to add SMA client credentials and pull plants/systems for each access token. */
export const SmaClientMeterPage = () => {
  const currentUser = useTracker(() => Meteor.user());
  if (!currentUser?.isSuperUser) return <NotPermittedPage />;
  const orgId = currentUser.orgId;
  const [username, setUsername] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [accessTokens, setAccessTokens] = useState<SmaClientAccessToken[]>([]);
  const [pullingTokens, setPullingTokens] = useState<string[]>([]);
  useEffect(() => {
    (async () => {
      if (!orgId) return;
      setAccessTokens(await getSmaClientAccessTokensForOrg.call(orgId));
    })();
  }, [orgId]);
  const save = toastOnError(async () => {
    if (!username || !password || !orgId) return;
    setSaving(true);
    try {
      await createSmaClientAccessToken.call({ username, password });
      setAccessTokens(await getSmaClientAccessTokensForOrg.call(orgId));
      setPassword('');
    }
    finally {
      setSaving(false);
    }
  });
  const pullPlants = toastOnError(async (tokenId: string) => {
    setPullingTokens([...pullingTokens, tokenId]);
    const { numPlants, numCreated } = await pullSmaClientPlantsForToken.call(tokenId);
    toast.success(`${numPlants} ${strings.SystemsFound()} | ${numCreated} ${strings.Created()}`);
    setPullingTokens(pullingTokens.filter((t) => t !== tokenId));
  });
  return (
    <SmaClientMeterPageStyled className="page">
      <PageHeading left={<h1>SMA Client</h1>} />
      <PageBody>
        <Form>
          <div className="field-group">
            <FormField field="username">
              <label>{strings.Username()}</label>
              <Input value={username} onChange={(username) => setUsername(username)} />
            </FormField>
            <FormField field="password">
              <label>{strings.Password()}</label>
              <Input value={password} type="password" onChange={(password) => setPassword(password)} />
            </FormField>
          </div>
          <div><Button onClick={() => save()}>{saving ? <Loading /> : strings.Save()}</Button></div>
        </Form>
        {
          accessTokens.length ?
            <div className="result">
              <h2>{strings.Accounts()}</h2>
              <table>
                <tbody>
                  {
                    accessTokens.map((t) => (
                      <tr key={t._id}>
                        <td>{t.username}</td>
                        <td><Button onClick={() => pullPlants(t._id)}>{pullingTokens.includes(t._id) ? <Loading /> : strings.PullSystems()}</Button></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            :
            null
        }
      </PageBody>
      <PageFooter />
    </SmaClientMeterPageStyled>
  );
};
